import React from 'react';
import { motion } from 'framer-motion';
import './LegalPage.css';

function AccessibilityStatement() {
  const isIOSSafari = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-safari');
  const inViewViewport = isIOSSafari ? { once: true, amount: 0.25 } : { once: true };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <main className="legal-page">
      <motion.div
        className="policy-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <h1 className="legal-title">Accessibility Statement</h1>
          <p className="policy-dates">Last Updated: June 18, 2026</p>
        </div>
      </motion.div>

      <motion.div
        className="container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={inViewViewport}
      >
        <article className="legal-card">
          <motion.section variants={itemVariants} className="legal-section">
            <p className="intro-text">
              Versa Launch is committed to making its website accessible to the widest possible
              audience, regardless of ability or technology. We are actively working to increase
              the accessibility and usability of our website and in doing so aim to adhere to
              available standards and guidelines.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>1. Conformance Target</h2>
            <p>
              We aim to conform to the{' '}
              <a href="https://www.w3.org/TR/WCAG21/" target="_blank" rel="noopener noreferrer">
                Web Content Accessibility Guidelines (WCAG) 2.1
              </a>{' '}
              at Level AA. These guidelines explain how to make web content more accessible to
              people with a wide range of disabilities, including visual, auditory, motor, and
              cognitive limitations.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>2. Measures We Take</h2>
            <ul>
              <li>Semantic HTML structure with headings, landmarks, and a "skip to content" link.</li>
              <li>Keyboard operability and visible focus indicators for interactive elements.</li>
              <li>Text alternatives for meaningful images and labels for form controls.</li>
              <li>Support for the operating system's reduced-motion preference.</li>
              <li>Color-contrast targets that meet WCAG AA for body text.</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>3. Known Limitations</h2>
            <p>
              Despite our efforts, some parts of the website may not yet be fully accessible. We
              are continually identifying and remediating issues, and we welcome reports that help
              us improve. Third-party content or embedded tools may not always meet the same
              standard; where this occurs we will work to provide an accessible alternative on
              request.
            </p>
          </motion.section>

          <motion.section variants={itemVariants} className="legal-section">
            <h2>4. Feedback &amp; Contact</h2>
            <p>
              If you encounter an accessibility barrier on this site, or need information in an
              alternative format, please contact us and we will respond as promptly as we can:
            </p>
            <ul>
              <li>Email: <a href="mailto:info@versalaunch.com">info@versalaunch.com</a></li>
              <li>Phone: <a href="tel:+19047504885">(904) 750-4885</a></li>
            </ul>
            <p>
              Please include the page address (URL) and a description of the difficulty so we can
              investigate quickly.
            </p>
          </motion.section>
        </article>
      </motion.div>
    </main>
  );
}

export default AccessibilityStatement;
